import type { FunctionComponent } from 'react';

import { Spinner } from '~/ui/core/spinner/Spinner';

import { WaveformTimeline } from './WaveformTimeline';

type RecordingOverlayProps = {
    readonly getAmplitude: () => number;
    readonly isFadingOut?: boolean;
    readonly isTranscribing: boolean;
    readonly recordingSeconds: number;
    readonly onStopRecording: () => void;
};

const formatTime = (seconds: number): string => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

export const RecordingOverlay: FunctionComponent<RecordingOverlayProps> = ({
    getAmplitude,
    isFadingOut = false,
    isTranscribing,
    recordingSeconds,
    onStopRecording,
}) => (
    <div
        className="absolute inset-0 z-10 flex items-center"
        style={{
            padding: '0 12px 0 20px',
            gap: 24,
            opacity: isFadingOut ? 0 : 1,
            transition: 'opacity 350ms ease',
            pointerEvents: isFadingOut ? 'none' : undefined,
        }}
    >
        <WaveformTimeline getAmplitude={getAmplitude} paused={isTranscribing || isFadingOut} />

        <span className="shrink-0 text-sm font-medium tabular-nums tracking-tight text-error-600">
            {formatTime(recordingSeconds)}
        </span>

        {isTranscribing ? (
            <div className="flex shrink-0 items-center justify-center" style={{ width: 24, height: 24 }}>
                <Spinner className="!mr-0 text-error-600" />
            </div>
        ) : (
            <button
                aria-label="Stop recording"
                className="flex shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-error-100 p-0 transition-opacity duration-200 hover:opacity-80"
                style={{ width: 24, height: 24 }}
                type="button"
                onClick={onStopRecording}
            >
                <div className="rounded-sm bg-error-600" style={{ width: 8, height: 8 }} />
            </button>
        )}
    </div>
);
